import { apiRequest } from '@/utils/api-request';

export async function getCarbonArcConnectUrl(
  orgId: string,
  returnTo?: string,
): Promise<string | null> {
  const params = new URLSearchParams({ org_id: orgId });
  if (returnTo) {
    params.set('return_to', returnTo);
  }
  const response = await apiRequest<{ authorize_url?: string }>({
    url: `/oauth/carbon-arc/start?${params.toString()}`,
    method: 'GET',
    target: 'platform',
  });
  if (response.detail) {
    throw new Error(response.detail);
  }
  return response.data?.authorize_url ?? null;
}

/** The three shapes `runtime-token` produces, minus the token itself. */
interface RuntimeTokenResponse {
  connected?: boolean;
  /** Set when the connection exists but a refresh failed transiently. */
  retryable?: boolean;
}

/**
 * Whether the current user has a working Carbon Arc connection.
 *
 * Reuses the endpoint the tool wrapper already calls at run time rather than a
 * status route of its own: it answers exactly this question, for this user and
 * org, from the same token row a run would resolve.
 *
 * Two costs come with that, both deliberate. Its response also carries the
 * decrypted provider token when connected — read here only for the flag and
 * never returned, so it stays out of the query cache and out of React state.
 * And its not-connected branch mints a single-use PKCE state that this caller
 * never redeems, leaving one short-lived key behind per check; the card mints
 * its own URL at click time regardless.
 *
 * @param orgId Org the connection is scoped to.
 * @returns True when connected, false when the user must authorize, and null
 *   when the answer is unknown — a transient refresh failure (the connection is
 *   fine, so nothing should be asked of the user) or an unreachable endpoint.
 */
export async function getCarbonArcConnectionStatus(
  orgId: string,
): Promise<boolean | null> {
  const params = new URLSearchParams({ org_id: orgId });
  const response = await apiRequest<RuntimeTokenResponse>({
    url: `/oauth/carbon-arc/runtime-token?${params.toString()}`,
    method: 'GET',
    target: 'platform',
    // Unlike the click-time mint, this check runs on its own whenever a
    // workflow holds a Carbon Arc node. In an environment where the
    // integration is not deployed the provider slug is unknown, and reporting
    // that on every such canvas load would say nothing new.
    suppressSentryStatuses: [404],
  });
  if (response.detail || !response.data) {
    return null;
  }
  const { connected, retryable } = response.data;
  // Only an explicit boolean is an answer. A body without the field is a shape
  // this does not recognise, and reading it as "not connected" would prompt.
  if (typeof connected !== 'boolean') {
    return null;
  }
  if (connected) {
    return true;
  }
  // Connected, but a refresh could not be completed right now. Prompting for
  // consent would be wrong: there is nothing for the user to fix.
  return retryable ? null : false;
}
